import React from "react";
import { SoonBadge } from "../../atoms/SoonBadge";
import { Spinner } from "../../atoms/Spinner";
import {
  ActionButton,
  ActionSlot,
  Actions,
  Body,
  Container,
  Fill,
  Detail,
  FixButton,
  IssueChip,
  IssueList,
  Leading,
  Meta,
  Title,
  Track,
} from "./StatusBanner.styles";
import type { StatusBannerAction, StatusBannerProps } from "./StatusBanner.types";

const renderAction = (flush?: boolean) => ({ id, label, onClick, disabled, soon }: StatusBannerAction) =>
  soon ? (
    <ActionSlot key={id}>
      <ActionButton $flush={flush} type="button" aria-disabled="true" data-action={id}>
        {label}
      </ActionButton>
      <SoonBadge />
    </ActionSlot>
  ) : (
    <ActionButton $flush={flush} type="button" key={id} disabled={disabled} onClick={onClick} data-action={id}>
      {label}
    </ActionButton>
  );

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

/**
 * Generic one-line status band: optional leading icon/spinner, title and detail, issue chips with
 * a Fix action, and trailing actions. Tones: default (tint), attention (amber), inverse (dark ink), neutral (light grey), error (pink). `leading="spinner"` shows a spinner; `actionItems` renders data-driven buttons (with `soon` teasers); `progress` (0-100) draws a thin bar.
 * Polite `role="status"` unless `role="alert"`. Stateless.
 */
export const StatusBanner: React.FC<StatusBannerProps> = ({
  leading,
  title,
  detail,
  issues,
  meta,
  actions,
  actionItems,
  progress,
  progressLabel,
  tone = "default",
  appearance = "default",
  flush = false,
  role = "status",
  className,
}) => (
  <Container
    $tone={tone}
    $appearance={appearance}
    $flush={flush}
    role={role}
    aria-live={role === "alert" ? "assertive" : "polite"}
    className={className}
    data-testid="status-banner"
    data-tone={tone}
    data-flush={flush || undefined}
  >
    {leading ? <Leading aria-hidden>{leading === "spinner" ? <Spinner size={14} /> : leading}</Leading> : null}
    <Body>
      {title ? <Title $strong={flush}>{title}</Title> : null}
      {detail ? <Detail $muted={flush && tone === "inverse"}>{detail}</Detail> : null}
      {issues?.length ? (
        <IssueList aria-label="Issues">
          {issues.map(({ id, label, actionLabel, onAction }) => (
            <IssueChip key={id} $tone={tone} data-testid="status-banner-issue">
              <span>{label}</span>
              {actionLabel && onAction ? (
                <FixButton type="button" onClick={onAction}>
                  {actionLabel}
                </FixButton>
              ) : null}
            </IssueChip>
          ))}
        </IssueList>
      ) : null}
    </Body>
    {meta ? <Meta>{meta}</Meta> : null}
    {actions || actionItems?.length ? (
      <Actions>
        {actions}
        {actionItems?.map(renderAction(flush))}
      </Actions>
    ) : null}
    {progress !== undefined ? (
      <Track role="progressbar" aria-label={progressLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clampPercent(progress))}>
        <Fill $value={clampPercent(progress)} />
      </Track>
    ) : null}
  </Container>
);

export default StatusBanner;
